import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import usePipApi, { PIP_DURATIONS } from './config';
import PipGoalsEditor from './PipGoalsEditor';
import PipDetailDrawer from './PipDetailDrawer';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import GenericSelect from '../../components/ui/GenericSelect';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import api from '../../services/api';

// P1 Görev Paketi A — PIP Süreçleri (İK yönetim ekranı): EmployeesPage aynası
// DataTable listesi (durum rozetleri config kolonlarında) + detay drawer
// (hedef editörü, check-in, başlat/kapat/iptal) + "Yeni PIP" form drawer'ı.
// UX standardı düzeltmesi (2026-08-10): oluşturma butonu DataTable'ın kendi
// "yeni kayıt" butonudur (tablo dışında buton YOK) ve oluşturma modal değil
// GenericFormDrawer kabuğuyla yapılır (GoalTemplatesTab deseni).
// Kalibrasyonda seçilen nihai seviye TriggersPip=true olan çalışanlar için
// taslaklar bu listeye OTOMATİK düşer (CalibrationDataAccess tetiği, Plan 2
// Task 8 — eski sabit "needs_improvement" token'ı yerine dönemin skalasındaki
// RatingScaleLevel.TriggersPip bayrağı) — İK burada süre + hedeflerle
// düzenleyip başlatır.
const Container = styled.div``;

const FieldGroup = styled.div`
  margin-bottom: 18px;
`;

// DevelopmentPlansPage.buildEmployeeAsync ile aynı uç/eşleme (fullName[like]).
function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const r = res.data?.data;
      const items = Array.isArray(r) ? r : (r?.data || []);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    },
  };
}

const employeeAsync = buildEmployeeAsync();

export default function PipPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const canManage = canWrite('PM');

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [drawer, setDrawer] = useState({ isOpen: false, pipId: null });

  // PM analitik P5 (E12) — Gelişim ve PIP analitiği listesinden derin bağlantı: ?pipId= detay çekmecesini açar
  // (okuma kapısı aynı: GET v1/pips/{id} yalnız İK; kapsam dışı kayıt 404).
  // Gelen Kutum da (pip_action kartı) buraya bağlanır. Çekmece kapanınca parametre history.replace ile
  // silinir (useDeepLinkParams): geri tuşu ya da sayfa yenileme kapatılmış kaydı yeniden açmaz.
  const { values: { pipId: linkedPipId }, clear: clearDeepLink } = useDeepLinkParams(['pipId']);
  useEffect(() => {
    if (linkedPipId) setDrawer({ isOpen: true, pipId: linkedPipId });
  }, [linkedPipId]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createEmployeeId, setCreateEmployeeId] = useState(null);
  const [createDuration, setCreateDuration] = useState(60);
  const [createGoals, setCreateGoals] = useState([]);

  const pipApi = usePipApi(queryParams);
  const {
    fetching, data, pagination, columns: baseColumns, create,
  } = pipApi;

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const openDrawer = useCallback((row) => {
    setDrawer({ isOpen: true, pipId: row.id });
  }, []);

  const openCreate = useCallback(() => {
    setCreateEmployeeId(null);
    setCreateDuration(60);
    setCreateGoals([]);
    setCreateOpen(true);
  }, []);

  // GenericFormDrawer sözleşmesi: false dönerse drawer açık kalır.
  const handleCreateSave = useCallback(async () => {
    if (!createEmployeeId) return false;
    const ok = await create({
      employeeId: createEmployeeId,
      durationDays: createDuration,
      goals: createGoals
        .filter((g) => g.title.trim())
        .map((g) => ({ title: g.title.trim(), successCriteria: g.successCriteria?.trim() || undefined })),
    });
    return !!ok;
  }, [createEmployeeId, createDuration, createGoals, create]);

  const durationOptions = useMemo(() => PIP_DURATIONS.map((d) => ({
    id: d,
    name: `${d} ${t('performance.pip.daysSuffix')}`,
  })), [t]);

  const buildRowActions = useCallback((row) => [
    { type: 'detail', label: t('performance.pip.viewDetail'), onClick: () => openDrawer(row) },
  ], [t, openDrawer]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          return <OptionsMenu actions={buildRowActions(row)} row={row} />;
        },
      },
    },
  ], [baseColumns, data, buildRowActions]);

  return (
    <Container>
      {/* Yeni PIP (manuel açılış — İK): GenericFormDrawer kabuk, içerik özel. */}
      <GenericFormDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t('performance.pip.newPip')}
        schema={[]}
        onSave={handleCreateSave}
      >
        <FieldGroup>
          <AsyncGenericSelect
            label={t('performance.pip.employee')}
            value={createEmployeeId}
            onChange={setCreateEmployeeId}
            onSearch={employeeAsync.onSearch}
            onGetById={employeeAsync.onGetById}
            required
          />
        </FieldGroup>
        <FieldGroup>
          <GenericSelect
            label={t('performance.pip.durationLabel')}
            options={durationOptions}
            value={createDuration}
            onChange={setCreateDuration}
          />
        </FieldGroup>
        <FieldGroup>
          <PipGoalsEditor goals={createGoals} onChange={setCreateGoals} />
        </FieldGroup>
      </GenericFormDrawer>

      {/* Liste tazelemesi usePipApi'nin mutasyon fonksiyonlarının içinde
          (refresh) zaten yapılır — drawer'a ayrıca onChanged geçilmez. */}
      <PipDetailDrawer
        pipId={drawer.pipId}
        open={drawer.isOpen}
        onClose={() => { setDrawer({ isOpen: false, pipId: null }); clearDeepLink(); }}
        canManage={canManage}
        pipApi={pipApi}
      />

      <DataTable
        title={t('performance.pip.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        canCreateNewRecord={canManage}
        newButtonLabel={t('performance.pip.newPip')}
        onNewButtonClick={openCreate}
      />
    </Container>
  );
}
